import {
    DecimalRange,
    ExtendedAreaModels,
    mergeDecimalRange,
    showPrice,
} from '@bpd-library/utilities';
import { FILTER_NAMES } from '@features/properties/components/property-filter';
import { getFilterUrlValue, hasFilterUrl } from '@source/utilities/filters';
import isRangeWithinRange from '@source/utilities/filters/is-range-in-range';
import { translateItem } from '@source/utilities/hooks';

import { Filters } from './construct-filters';

export const getPrices = (data: ExtendedAreaModels): { min: number; max: number } => {
    const constructionNumberAmounts = data.constructionNumbers.reduce<DecimalRange[]>(
        (amounts, constructionNumber) => {
            if (!showPrice({ constructionNumber })) return amounts;

            const amount =
                constructionNumber.salesPrice?.amount || constructionNumber.rentalPrice?.amount;

            if (amount) amounts.push(amount);

            return amounts;
        },
        [],
    );

    const propertyTypeAmounts = data.propertyTypes.reduce<DecimalRange[]>(
        (amounts, propertyType) => {
            if (propertyType.constructionNumbers?.length || !showPrice({ propertyType }))
                return amounts;

            const amount = propertyType.salesPrice?.amount || propertyType.rentalPrice?.amount;

            if (amount) amounts.push(amount);

            return amounts;
        },
        [],
    );

    const range = [...constructionNumberAmounts, ...propertyTypeAmounts].reduce<DecimalRange>(
        mergeDecimalRange,
        {},
    );

    return {
        min: range.from!,
        max: range.to!,
    };
};

export const setPriceFilters = async (data: ExtendedAreaModels, filters: Filters) => {
    const filterName = FILTER_NAMES.priceRange;
    const currentFilter = getFilterUrlValue(filterName)?.split('.');
    const hasSale = !!data.projectPhases.find(({ isRental }) => !isRental);
    const increment = hasSale ? 5000 : 100;

    const prices = getPrices(data);

    if (prices.min && prices.max) {
        if (prices.min === prices.max) prices.max += increment;

        let min: number = (currentFilter && parseInt(currentFilter[0])) || prices.min;
        let max: number = (currentFilter && parseInt(currentFilter[1])) || prices.max;

        const handlePriceChange = ({
            minValue,
            maxValue,
        }: {
            minValue: number;
            maxValue: number;
        }) => {
            min = minValue;
            max = maxValue;
        };

        const updatedPrice: { min: number; max: number } = {
            min: prices.min,
            max: prices.max,
        };

        filters[filterName] = {
            expanded: false,
            icon: 'specs-price',
            title: await translateItem('Filters.Price.label'),
            dropdownLabel: await translateItem('Filters.Price.dropdownLabel'),
            filterFunctions: {
                constructionNumber: (constructionNumber, filterInput) => {
                    if (
                        constructionNumber.salesPrice?.priceType === 'PriceOnRequest' ||
                        constructionNumber.rentalPrice?.priceType === 'PriceOnRequest'
                    )
                        return true;

                    if (!showPrice({ constructionNumber })) return false;

                    if (
                        !constructionNumber.salesPrice?.amount &&
                        !constructionNumber.rentalPrice?.amount
                    )
                        return false;

                    if (filterInput.id === `${filterName}-0`) {
                        updatedPrice.min = parseInt(filterInput.value);
                    } else {
                        updatedPrice.max = parseInt(filterInput.value);
                    }

                    const range =
                        filterInput.id === `${filterName}-0`
                            ? {
                                  from: parseInt(filterInput.value),
                                  to: updatedPrice.max,
                              }
                            : {
                                  from: updatedPrice.min,
                                  to: parseInt(filterInput.value),
                              };

                    return isRangeWithinRange(
                        range,
                        constructionNumber.salesPrice?.amount! ||
                            constructionNumber.rentalPrice?.amount,
                    );
                },
                propertyType: (propertyType, filterInput) => {
                    if (
                        propertyType.salesPrice?.priceType === 'PriceOnRequest' ||
                        propertyType.rentalPrice?.priceType === 'PriceOnRequest'
                    )
                        return true;

                    if (!showPrice({ propertyType })) return false;

                    if (!propertyType.salesPrice?.amount && !propertyType.rentalPrice?.amount)
                        return false;

                    if (filterInput.id === `${filterName}-0`) {
                        updatedPrice.min = parseInt(filterInput.value);
                    } else {
                        updatedPrice.max = parseInt(filterInput.value);
                    }

                    const range =
                        filterInput.id === `${filterName}-0`
                            ? {
                                  from: parseInt(filterInput.value),
                                  to: updatedPrice.max,
                              }
                            : {
                                  from: updatedPrice.min,
                                  to: parseInt(filterInput.value),
                              };

                    return isRangeWithinRange(
                        range,
                        propertyType.salesPrice?.amount! || propertyType.rentalPrice?.amount,
                    );
                },
                projectPhase: (projectPhase, filterInput) => {
                    if (
                        projectPhase.propertyPurchasePrice?.priceType === 'PriceOnRequest' ||
                        projectPhase.propertyRentalPrice?.priceType === 'PriceOnRequest'
                    )
                        return true;

                    if (
                        !projectPhase.propertyPurchasePrice?.amount &&
                        !projectPhase.propertyRentalPrice?.amount
                    )
                        return false;

                    if (filterInput.id === `${filterName}-0`) {
                        updatedPrice.min = parseInt(filterInput.value);
                    } else {
                        updatedPrice.max = parseInt(filterInput.value);
                    }

                    const range =
                        filterInput.id === `${filterName}-0`
                            ? {
                                  from: parseInt(filterInput.value),
                                  to: updatedPrice.max,
                              }
                            : {
                                  from: updatedPrice.min,
                                  to: parseInt(filterInput.value),
                              };

                    return isRangeWithinRange(
                        range,
                        projectPhase.propertyPurchasePrice?.amount! ||
                            projectPhase.propertyRentalPrice?.amount,
                    );
                },
            },
            filterName,
            filterLogic: '&&',
            hasRangeValues: true,
            filters: {
                type: 'range',
                components: [
                    {
                        label: `${filterName}-0`,
                        value: `${min}`,
                        id: `${filterName}-0`,
                        checked: hasFilterUrl(`${min}`, filterName),
                        name: filterName,
                        type: 'number',
                    },
                    {
                        label: `${filterName}-1`,
                        value: `${max}`,
                        id: `${filterName}-1`,
                        checked: hasFilterUrl(`${max}`, filterName),
                        name: filterName,
                        type: 'number',
                    },
                ],
                rangeSliderOptions: {
                    min: prices.min,
                    max: prices.max,
                    increment,
                    start: [min, max],
                    onChange: (e: CustomEvent) => handlePriceChange(e.detail),
                    name: filterName,
                },
            },
        };
    }
};
